import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AssignmentPickList from '../../../components/ui/AssignmentPickList'
import FormDialog from '../../../components/ui/FormDialog'
import EmptyState from '../../../components/ui/EmptyState'
import Badge from '../../../components/ui/Badge'
import { getRoleGrants } from '../api/rolesApi'
import type { Grant, Role } from '../types'

interface Props {
  visible: boolean
  role: Role | null
  grantOptions: Grant[]
  submitting: boolean
  onSubmit: (grantIds: number[]) => void
  onHide: () => void
  /**
   * Görüntüleme kipi (126, Kısıtlı Yetkili asistan): rolün yetkileri yalnız bu pencerede görünür, o
   * yüzden pencere kapanmaz, salt okunur açılır — kaydet yok, çift liste yerine atanmış yetkiler düz
   * çip listesi olarak okunur (taşınabilen ama kaydedilemeyen bir liste yanıltıcı olurdu).
   */
  readOnly?: boolean
}

/** Role yetki atama diyaloğu (set semantiği). Açılışta mevcut atanmış yetkiler yüklenir; MultiSelect. */
export default function RoleGrantsDialog({
  visible,
  role,
  grantOptions,
  submitting,
  onSubmit,
  onHide,
  readOnly = false,
}: Readonly<Props>) {
  const { t, i18n } = useTranslation()
  const [selected, setSelected] = useState<number[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (visible && role) {
      // Satır değişince önceki seçimi hemen temizle (stale gösterim olmasın).
      setSelected([])
      setLoadError(false)
      setLoading(true)
      getRoleGrants(role.id)
        .then((ids) => {
          if (!cancelled) setSelected(ids)
        })
        .catch(() => {
          if (!cancelled) setLoadError(true)
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }
    return () => {
      cancelled = true
    }
  }, [visible, role])

  const en = i18n.language.startsWith('en')
  const items = grantOptions.map((g) => ({ id: g.id, label: en && g.nameEn ? g.nameEn : g.nameTr }))
  const assigned = items
    .filter((i) => selected.includes(i.id))
    .sort((a, b) => a.label.localeCompare(b.label, en ? 'en' : 'tr'))

  // Başlığın yanında hangi role yetki atandığı görünsün (dil seçimine göre rol adı).
  const roleLabel = role ? (en && role.nameEn ? role.nameEn : role.nameTr) : ''

  const renderBody = () => {
    if (loadError) return <EmptyState title={t('admin.roles.grantsDialog.loadError')} />
    if (readOnly) {
      if (loading) return null
      if (assigned.length === 0) return <EmptyState title={t('admin.roles.grantsDialog.empty')} />
      return (
        <div className="admin-role-chips">
          {assigned.map((g) => (
            <Badge key={g.id} tone="neutral">
              {g.label}
            </Badge>
          ))}
        </div>
      )
    }
    if (items.length === 0 && !loading) {
      return <EmptyState title={t('admin.roles.grantsDialog.empty')} />
    }
    return (
      <AssignmentPickList
        items={items}
        value={selected}
        onChange={setSelected}
        sourceHeader={t('admin.roles.grantsDialog.sourceHeader')}
        targetHeader={t('admin.roles.grantsDialog.targetHeader')}
        filterPlaceholder={t('common.searchPlaceholder')}
      />
    )
  }

  return (
    <FormDialog
      visible={visible}
      width={readOnly ? '40rem' : '72rem'}
      // Görüntülemede "Role Yetki Ata" başlığı yanıltırdı; listenin kendi başlığı kullanılır
      // ("Atanmış Yetkiler").
      header={t(readOnly ? 'admin.roles.grantsDialog.targetHeader' : 'admin.roles.grantsDialog.title')}
      headerContext={roleLabel}
      submitLabel={t('admin.roles.grantsDialog.save')}
      submitting={submitting || loading}
      // Yükleme hatasında kaydetme kilitli — aksi halde boş seçim tüm yetkileri siler (veri kaybı).
      submitDisabled={loading || loadError}
      onSubmit={() => onSubmit(selected)}
      onHide={onHide}
      readOnly={readOnly}
    >
      <div className="admin-form">{renderBody()}</div>
    </FormDialog>
  )
}
